import React, { useState, useEffect } from 'react';
import {
  Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, Grid, IconButton, InputLabel, MenuItem, Paper, Select,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography, Chip, Tabs, Tab
} from '@mui/material';
import { Add, Delete, Celebration, AttachMoney, Receipt, Download } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { getApartments, getApartmentUsers, getFestivals, createFestival, deleteFestival, getFestivalContributions, createFestivalContribution, deleteFestivalContribution, getFestivalExpenses, createFestivalExpense, deleteFestivalExpense, exportFestival } from '../services/api';

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

export default function Festivals() {
  const [apartments, setApartments] = useState([]);
  const [selectedApartment, setSelectedApartment] = useState('');
  const [apartmentUsers, setApartmentUsers] = useState([]);
  const [festivals, setFestivals] = useState([]);
  const [selectedFestival, setSelectedFestival] = useState(null);
  const [contributions, setContributions] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [tab, setTab] = useState(0);
  const [openFestival, setOpenFestival] = useState(false);
  const [openContrib, setOpenContrib] = useState(false);
  const [openExpense, setOpenExpense] = useState(false);
  const [festivalForm, setFestivalForm] = useState({ name: '', description: '', festivalDate: '' });
  const [contribForm, setContribForm] = useState({ apartmentUserId: '', flatNumber: '', amount: '', contributionDate: '', notes: '' });
  const [expenseForm, setExpenseForm] = useState({ description: '', amount: '', expenseDate: '', notes: '' });
  const [expenseBillImage, setExpenseBillImage] = useState(null);
  const { isAdmin } = useAuth();

  useEffect(() => {
    getApartments().then(res => {
      setApartments(res.data);
      if (res.data.length > 0) setSelectedApartment(res.data[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedApartment) return;
    getFestivals(selectedApartment).then(res => setFestivals(res.data)).catch(() => {});
    getApartmentUsers(selectedApartment).then(res => setApartmentUsers(res.data)).catch(() => {});
  }, [selectedApartment]);

  useEffect(() => {
    if (!selectedFestival) return;
    getFestivalContributions(selectedFestival.id).then(res => setContributions(res.data)).catch(() => {});
    getFestivalExpenses(selectedFestival.id).then(res => setExpenses(res.data)).catch(() => {});
  }, [selectedFestival]);

  const handleCreateFestival = async () => {
    await createFestival({ apartmentId: selectedApartment, ...festivalForm });
    setOpenFestival(false);
    setFestivalForm({ name: '', description: '', festivalDate: '' });
    getFestivals(selectedApartment).then(res => setFestivals(res.data));
  };

  const handleAddContrib = async () => {
    await createFestivalContribution({ festivalId: selectedFestival.id, ...contribForm, amount: parseFloat(contribForm.amount) });
    setOpenContrib(false);
    setContribForm({ apartmentUserId: '', flatNumber: '', amount: '', contributionDate: '', notes: '' });
    getFestivalContributions(selectedFestival.id).then(res => setContributions(res.data));
    getFestivals(selectedApartment).then(res => setFestivals(res.data));
  };

  const handleAddExpense = async () => {
    await createFestivalExpense({ festivalId: selectedFestival.id, ...expenseForm, amount: parseFloat(expenseForm.amount), billImage: expenseBillImage });
    setOpenExpense(false);
    setExpenseForm({ description: '', amount: '', expenseDate: '', notes: '' });
    setExpenseBillImage(null);
    getFestivalExpenses(selectedFestival.id).then(res => setExpenses(res.data));
    getFestivals(selectedApartment).then(res => setFestivals(res.data));
  };

  const totalContrib = contributions.reduce((s, c) => s + c.amount, 0);
  const totalExp = expenses.reduce((s, e) => s + e.amount, 0);

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Festivals</Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Apartment</InputLabel>
            <Select value={selectedApartment} label="Apartment" onChange={(e) => { setSelectedApartment(e.target.value); setSelectedFestival(null); }}>
              {apartments.map(a => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          {isAdmin() && (
            <Button variant="contained" startIcon={<Add />} onClick={() => setOpenFestival(true)}>Add Festival</Button>
          )}
        </Grid>
      </Grid>

      {/* Festival List */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {festivals.map(f => (
          <Grid item xs={12} sm={6} md={4} key={f.id}>
            <Card
              sx={{ cursor: 'pointer', border: selectedFestival?.id === f.id ? '2px solid #1976d2' : '1px solid #e0e0e0', '&:hover': { boxShadow: 3 } }}
              onClick={() => setSelectedFestival(f)}
            >
              <CardContent sx={{ py: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1" fontWeight={600}><Celebration sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />{f.name}</Typography>
                  {isAdmin() && <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); deleteFestival(f.id).then(() => getFestivals(selectedApartment).then(res => setFestivals(res.data))); }}><Delete fontSize="small" /></IconButton>}
                </Box>
                <Typography variant="caption" color="text.secondary">{new Date(f.festivalDate).toLocaleDateString()}</Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Chip label={`₹${f.totalContributions.toFixed(0)} collected`} size="small" color="success" variant="outlined" />
                  <Chip label={`₹${f.totalExpenses.toFixed(0)} spent`} size="small" color="error" variant="outlined" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
        {festivals.length === 0 && <Grid item xs={12}><Typography color="text.secondary">No festivals added yet</Typography></Grid>}
      </Grid>

      {/* Selected Festival Details */}
      {selectedFestival && (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6">{selectedFestival.name}</Typography>
            <Box>
              <Button size="small" variant="outlined" startIcon={<Download />} sx={{ mr: 1 }}
                onClick={async () => {
                  try {
                    const res = await exportFestival(selectedFestival.id);
                    const url = window.URL.createObjectURL(new Blob([res.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `Festival_${selectedFestival.name}.xlsx`;
                    link.click();
                  } catch { alert('Failed to export'); }
                }}>Export</Button>
              <Chip label={`Collected: ₹${totalContrib.toFixed(2)}`} color="success" sx={{ mr: 1 }} />
              <Chip label={`Spent: ₹${totalExp.toFixed(2)}`} color="error" sx={{ mr: 1 }} />
              <Chip label={`Balance: ₹${(totalContrib - totalExp).toFixed(2)}`} color={totalContrib - totalExp >= 0 ? 'info' : 'warning'} />
            </Box>
          </Box>

          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
            <Tab label="Contributions" icon={<AttachMoney />} iconPosition="start" />
            <Tab label="Expenses" icon={<Receipt />} iconPosition="start" />
          </Tabs>

          {/* Contributions Tab */}
          <TabPanel value={tab} index={0}>
            {isAdmin() && <Button size="small" variant="outlined" startIcon={<Add />} sx={{ mb: 1 }} onClick={() => setOpenContrib(true)}>Add Contribution</Button>}
            <TableContainer>
              <Table size="small">
                <TableHead><TableRow><TableCell>Date</TableCell><TableCell>Flat</TableCell><TableCell>Resident</TableCell><TableCell align="right">Amount</TableCell><TableCell>Notes</TableCell>{isAdmin() && <TableCell />}</TableRow></TableHead>
                <TableBody>
                  {contributions.map(c => (
                    <TableRow key={c.id}>
                      <TableCell>{new Date(c.contributionDate).toLocaleDateString()}</TableCell>
                      <TableCell>{c.flatNumber}</TableCell>
                      <TableCell>{c.userName}</TableCell>
                      <TableCell align="right">₹{c.amount.toFixed(2)}</TableCell>
                      <TableCell>{c.notes || '—'}</TableCell>
                      {isAdmin() && <TableCell><IconButton size="small" color="error" onClick={() => deleteFestivalContribution(c.id).then(() => { getFestivalContributions(selectedFestival.id).then(res => setContributions(res.data)); getFestivals(selectedApartment).then(res => setFestivals(res.data)); })}><Delete fontSize="small" /></IconButton></TableCell>}
                    </TableRow>
                  ))}
                  {contributions.length === 0 && <TableRow><TableCell colSpan={6} align="center"><Typography color="text.secondary">No contributions yet</Typography></TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* Expenses Tab */}
          <TabPanel value={tab} index={1}>
            {isAdmin() && <Button size="small" variant="outlined" startIcon={<Add />} sx={{ mb: 1 }} onClick={() => setOpenExpense(true)}>Add Expense</Button>}
            <TableContainer>
              <Table size="small">
                <TableHead><TableRow><TableCell>Date</TableCell><TableCell>Description</TableCell><TableCell align="right">Amount</TableCell><TableCell>Bill</TableCell><TableCell>Notes</TableCell><TableCell>Added By</TableCell>{isAdmin() && <TableCell />}</TableRow></TableHead>
                <TableBody>
                  {expenses.map(e => (
                    <TableRow key={e.id}>
                      <TableCell>{new Date(e.expenseDate).toLocaleDateString()}</TableCell>
                      <TableCell>{e.description}</TableCell>
                      <TableCell align="right">₹{e.amount.toFixed(2)}</TableCell>
                      <TableCell>{e.billImageUrl ? <a href={e.billImageUrl} target="_blank" rel="noopener noreferrer">View</a> : '—'}</TableCell>
                      <TableCell>{e.notes || '—'}</TableCell>
                      <TableCell>{e.createdByUser}</TableCell>
                      {isAdmin() && <TableCell><IconButton size="small" color="error" onClick={() => deleteFestivalExpense(e.id).then(() => { getFestivalExpenses(selectedFestival.id).then(res => setExpenses(res.data)); getFestivals(selectedApartment).then(res => setFestivals(res.data)); })}><Delete fontSize="small" /></IconButton></TableCell>}
                    </TableRow>
                  ))}
                  {expenses.length === 0 && <TableRow><TableCell colSpan={7} align="center"><Typography color="text.secondary">No expenses yet</Typography></TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
        </Paper>
      )}

      {/* Create Festival Dialog */}
      <Dialog open={openFestival} onClose={() => setOpenFestival(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Festival</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Festival Name" margin="normal" value={festivalForm.name} onChange={(e) => setFestivalForm({ ...festivalForm, name: e.target.value })} placeholder="e.g. Diwali 2026" />
          <TextField fullWidth label="Description (Optional)" margin="normal" value={festivalForm.description} onChange={(e) => setFestivalForm({ ...festivalForm, description: e.target.value })} />
          <TextField fullWidth label="Festival Date" type="date" margin="normal" InputLabelProps={{ shrink: true }} value={festivalForm.festivalDate} onChange={(e) => setFestivalForm({ ...festivalForm, festivalDate: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenFestival(false)}>Cancel</Button>
          <Button onClick={handleCreateFestival} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Add Contribution Dialog */}
      <Dialog open={openContrib} onClose={() => setOpenContrib(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Festival Contribution</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Flat / Resident</InputLabel>
            <Select value={contribForm.apartmentUserId} label="Flat / Resident" onChange={(e) => {
              const au = apartmentUsers.find(u => u.id === e.target.value);
              setContribForm({ ...contribForm, apartmentUserId: e.target.value, flatNumber: au?.flatNumber || '' });
            }}>
              {apartmentUsers.map(au => <MenuItem key={au.id} value={au.id}>{au.flatNumber} - {au.userName}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField fullWidth label="Amount (₹)" type="number" margin="normal" value={contribForm.amount} onChange={(e) => setContribForm({ ...contribForm, amount: e.target.value })} />
          <TextField fullWidth label="Date" type="date" margin="normal" InputLabelProps={{ shrink: true }} value={contribForm.contributionDate} onChange={(e) => setContribForm({ ...contribForm, contributionDate: e.target.value })} />
          <TextField fullWidth label="Notes (Optional)" margin="normal" value={contribForm.notes} onChange={(e) => setContribForm({ ...contribForm, notes: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenContrib(false)}>Cancel</Button>
          <Button onClick={handleAddContrib} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>

      {/* Add Expense Dialog */}
      <Dialog open={openExpense} onClose={() => { setOpenExpense(false); setExpenseBillImage(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Add Festival Expense</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Description" margin="normal" value={expenseForm.description} onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })} placeholder="e.g. Decoration, Sweets, DJ" />
          <TextField fullWidth label="Amount (₹)" type="number" margin="normal" value={expenseForm.amount} onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })} />
          <TextField fullWidth label="Date" type="date" margin="normal" InputLabelProps={{ shrink: true }} value={expenseForm.expenseDate} onChange={(e) => setExpenseForm({ ...expenseForm, expenseDate: e.target.value })} />
          <TextField fullWidth label="Notes (Optional)" margin="normal" value={expenseForm.notes} onChange={(e) => setExpenseForm({ ...expenseForm, notes: e.target.value })} />
          <Box sx={{ mt: 2, p: 1.5, border: '1px dashed #ccc', borderRadius: 1, textAlign: 'center' }}>
            <Button variant="outlined" component="label" size="small">
              {expenseBillImage ? expenseBillImage.name : 'Upload Bill Image (Optional)'}
              <input type="file" hidden accept=".jpg,.jpeg,.png,.pdf,.webp"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file && file.size > 2 * 1024 * 1024) { alert('File must be less than 2MB'); return; }
                  setExpenseBillImage(file);
                }} />
            </Button>
            {expenseBillImage && <Typography variant="caption" display="block" color="text.secondary">{(expenseBillImage.size / 1024).toFixed(1)} KB</Typography>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenExpense(false); setExpenseBillImage(null); }}>Cancel</Button>
          <Button onClick={handleAddExpense} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
